import React from 'react';
import { css } from '@emotion/react';

import NvIcon from 'shared/components/nv-icon';
import { halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray5, gray7, primary } from 'styles/global_defaults/colors';
import { config } from '@config/pendo.config.json';

export type Avatar = {
  id: string;
  name: string;
  displayUrl: string;
  gender: 'male' | 'female';
  facePresetKey: string;
};

const avatarOptionStyles = css`
  border: 1px solid ${gray5};
  border-radius: ${quarterSpacing}px;

  &.selected, &.focused {
    border-color: ${primary};
    outline: 1px solid ${primary};
    outline-offset: 0;
  }

  .avatar-image {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  .radio-indicator {
    width: ${standardSpacing}px;
    height: ${standardSpacing}px;
    border: 1px solid ${gray5};
    border-radius: 50%;
    color: ${gray7};
  }

  &.selected .radio-indicator {
    border: none;
    background-color: ${primary};
  }
`;

type AvatarOptionProps = {
  avatar: Avatar;
  isSelected: boolean;
  onSelect: () => void;
};

const AvatarOption = ({
  avatar,
  isSelected,
  onSelect,
}: AvatarOptionProps) => {
  const [isFocused, setIsFocused] = React.useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      css={avatarOptionStyles}
      className={`d-flex align-items-center px-2 py-1 ${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      data-qa={config.pendo.activities.aiRolePlay.configModal.editCharacterVoiceOption}
      data-qa-id={`${config.pendo.activities.aiRolePlay.configModal.editCharacterVoiceOption}-${avatar.id}`}
    >
      <div className='d-flex align-items-center' css={css`gap: ${halfSpacing}px;`}>
        <img
          className='avatar-image'
          src={avatar.displayUrl}
          alt={avatar.name}
        />
        <div className='text-medium font-weight-bold text-black'>
          {avatar.name}
        </div>
      </div>
      <div className='radio-indicator d-flex align-items-center justify-content-center ml-auto flex-shrink-0'>
        {isSelected && <NvIcon icon='check' size='xss-smallest' />}
      </div>
    </div>
  );
};

export default AvatarOption;
